import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

interface ApplyContextType {
  basePath: string;
  pendingId: string | null;
  isLoading: boolean;
  /** In-memory SSN — never persisted to storage */
  ssnValue: string;
  setSsnValue: (v: string) => void;
  /** Server-issued submission token */
  submissionToken: string | null;
  /** True when this custom_id has already been submitted */
  alreadySubmitted: boolean;
  /** Most recent loan_applications.id for this custom_id (when submitted) */
  latestApplicationId: string | null;
  /** True after the user explicitly chose to resubmit */
  resubmitRequested: boolean;
  setResubmitRequested: (v: boolean) => void;
}

const ApplyContext = createContext<ApplyContextType>({
  basePath: "/apply",
  pendingId: null,
  isLoading: false,
  ssnValue: "",
  setSsnValue: () => {},
  submissionToken: null,
  alreadySubmitted: false,
  latestApplicationId: null,
  resubmitRequested: false,
  setResubmitRequested: () => {},
});

export const useApplyContext = () => useContext(ApplyContext);

export const ApplyProvider = ({ children }: { children: ReactNode }) => {
  const { customId, urlCode } = useParams<{ customId: string; urlCode: string }>();
  const [searchParams] = useSearchParams();
  const urlPiiToken = searchParams.get("token");
  const storedPiiToken = customId ? sessionStorage.getItem(`pendingPiiToken:${customId}`) : null;
  const piiToken = urlPiiToken || storedPiiToken;
  const [isLoading, setIsLoading] = useState(!!customId);
  const [ssnValue, setSsnValue] = useState("");
  const [submissionToken, setSubmissionToken] = useState<string | null>(null);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [latestApplicationId, setLatestApplicationId] = useState<string | null>(null);
  const [resubmitRequested, setResubmitRequestedState] = useState<boolean>(() => {
    if (typeof window === "undefined" || !customId) return false;
    return sessionStorage.getItem(`resubmitRequested:${customId}`) === "1";
  });
  const setResubmitRequested = (v: boolean) => {
    setResubmitRequestedState(v);
    if (customId) {
      if (v) sessionStorage.setItem(`resubmitRequested:${customId}`, "1");
      else sessionStorage.removeItem(`resubmitRequested:${customId}`);
    }
  };

  const basePath = customId && urlCode ? `/${urlCode}/${customId}/apply` : "/apply";

  useEffect(() => {
    if (customId && urlPiiToken) {
      sessionStorage.setItem(`pendingPiiToken:${customId}`, urlPiiToken);
    }
  }, [customId, urlPiiToken]);

  // Fetch a submission token at the start of the flow
  useEffect(() => {
    const fetchSubmissionToken = async () => {
      try {
        const { data, error } = await supabase.functions.invoke("generate-submission-token", {
          body: {},
        });
        if (!error && data?.submission_token) {
          setSubmissionToken(data.submission_token);
        } else {
          console.error("Failed to get submission token:", error || data);
        }
      } catch (e) {
        console.error("Error fetching submission token:", e);
      }
    };
    fetchSubmissionToken();
  }, []);

  useEffect(() => {
    setSsnValue("");
    if (!customId) {
      return;
    }

    const fetchPendingApplication = async () => {
      // Fetch non-sensitive fields via RPC
      let data: any = null;
      let error: any = null;
      try {
        const res = await supabase
          .rpc("get_pending_application_by_custom_id", { _custom_id: customId })
          .single();
        data = res.data;
        error = res.error;
      } catch (e) {
        error = e;
      }

      if (data && !error) {
        // Detect submitted state and look up latest loan_applications row
        const isSubmitted = !!(data as Record<string, unknown>).submitted;
        setAlreadySubmitted(isSubmitted);
        if (isSubmitted) {
          try {
            const { data: latestId } = await (supabase.rpc as unknown as (
              fn: string,
              args: Record<string, unknown>
            ) => Promise<{ data: string | null; error: unknown }>)(
              "get_latest_application_id_by_custom_id",
              { _custom_id: customId }
            );
            if (latestId) setLatestApplicationId(latestId);
          } catch (e) {
            console.error("Failed to look up latest application:", e);
          }
        }

        const mapped: Record<string, string> = {};
        // Non-PII fields from RPC
        const rpcFields = [
          'loan_purpose', 'loan_amount', 'credit_score', 'payment_frequency', 'employment_status',
          'annual_income', 'housing_status', 'education_level'
        ] as const;
        for (const key of rpcFields) {
          const val = (data as Record<string, unknown>)[key];
          if (val != null && val !== '') {
            mapped[key] = String(val);
          }
        }

        // Fetch PII fields via edge function (requires signed token retained in URL or sessionStorage)
        if (piiToken) {
          try {
            const { data: piiData, error: piiError } = await supabase.functions.invoke("get-pending-pii", {
              body: { custom_id: customId, token: piiToken },
            });
            if (!piiError && piiData?.data) {
              const piiFields = [
                'first_name', 'last_name', 'date_of_birth', 'email', 'phone',
                'street_address', 'apt_unit', 'city', 'state', 'zip_code'
              ] as const;
              for (const key of piiFields) {
                const val = piiData.data[key];
                if (val != null && val !== '') {
                  let str = String(val);
                  // Normalize DOB from YYYY-MM-DD to MM/DD/YYYY for the UI
                  if (key === 'date_of_birth') {
                    const m = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
                    if (m) str = `${m[2]}/${m[3]}/${m[1]}`;
                  }
                  mapped[key] = str;
                }
              }
            }
          } catch (e) {
            console.error("Failed to fetch PII for prefill:", e);
          }

          // If SSN is encrypted, decrypt it and store in-memory only
          if ((data as Record<string, unknown>).has_ssn) {
            try {
              const { data: ssnData, error: ssnError } = await supabase.functions.invoke("decrypt-ssn", {
                body: { custom_id: customId, token: piiToken },
              });
              if (!ssnError && ssnData?.ssn) {
                const raw = ssnData.ssn;
                if (raw.length === 9) {
                  setSsnValue(`${raw.slice(0, 3)}-${raw.slice(3, 5)}-${raw.slice(5)}`);
                } else {
                  setSsnValue(raw);
                }
              }
            } catch (e) {
              console.error("Failed to decrypt SSN for prefill:", e);
            }
          } else {
            setSsnValue("");
          }
        }

        // Merge with existing sessionStorage so PII isn't wiped when the page is
        // refreshed/navigated without the ?token= query param. SSN is NOT stored.
        const existing = JSON.parse(sessionStorage.getItem("loanApplicationData") || "{}");
        sessionStorage.setItem("loanApplicationData", JSON.stringify({ ...existing, ...mapped }));
      }
      setIsLoading(false);
    };

    fetchPendingApplication();
  }, [customId, piiToken]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Loading application...</p>
      </div>
    );
  }

  return (
    <ApplyContext.Provider value={{ basePath, pendingId: customId || null, isLoading, ssnValue, setSsnValue, submissionToken, alreadySubmitted, latestApplicationId, resubmitRequested, setResubmitRequested }}>
      {children}
    </ApplyContext.Provider>
  );
};
